import { diContainer } from '../DIContainer';
import { AppStorageKey } from '../types';
import { I18n } from '../wrappers/I18n';
import Device from '../utilities/Device';
import Settings from '../wrappers/Settings';
import { CapacitorSdc } from 'capacitor-sdc';
export default class FiscalServiceAndroid {
    sdcUrl;
    pac;
    certAlias;
    deviceSupported;
    permissionsAllowed;
    sdc = CapacitorSdc;
    buildPluginOptions(sdc, url) {
        return {
            url,
            pac: this.pac,
            certAlias: this.certAlias,
            sdc,
        };
    }
    async init(options) {
        const { esdcUrl } = options;
        const appStorage = diContainer.resolve('appStorage');
        this.sdcUrl = esdcUrl;
        this.certAlias = await appStorage.native().getItem(AppStorageKey.sdcCertAlias) || undefined;
        this.pac = await appStorage.native().getItem(AppStorageKey.sdcCertPac) || undefined;
        // Check if device is supported
        const supportedDevices = [
            { manufacturer: 'szzcs', model: 'Z91Pro' },
            { manufacturer: 'ZCS', model: 'Z91Pro' },
            { manufacturer: 'szzcs', model: 'Z92' },
            { manufacturer: 'ZCS', model: 'Z92' },
            { manufacturer: 'szzcs', model: 'Z100' },
            { manufacturer: 'ZCS', model: 'Z100' },
            { manufacturer: 'szzcs', model: 'digitron ist Z91' },
            { manufacturer: 'ZCS', model: 'digitron ist Z91' },
            { manufacturer: 'digitron ist', model: 'digitron ist Z91' },
            { manufacturer: 'Ciontek', model: 'CS20' },
            { manufacturer: 'Ciontek', model: 'CS30' },
            { manufacturer: 'BLESSX', model: 'any' },
            { manufacturer: 'UMIDIGI', model: 'any' },
        ];
        const device = Device.get();
        const manufacturer = device?.manufacturer;
        const model = device?.model;
        const modelSupported = supportedDevices.some(device => device.manufacturer === manufacturer && (device.model === 'any' || device.model === model));
        // An external USB CCID smart-card reader makes any Android device an ESDC —
        // the native driver talks to it directly, no vendor SDK involved
        const usbReaderAttached = !modelSupported && (await this.sdc.hasUsbCcidReader().catch(() => ({ attached: false }))).attached;
        if (!modelSupported && !usbReaderAttached) {
            console.log('ESDC not supported on this device');
            this.deviceSupported = false;
            return;
        }
        this.deviceSupported = true;
        this.permissionsAllowed = await this.checkPermissions();
        if (this.permissionsAllowed) {
            const options = this.buildPluginOptions('ESDC', this.getSdcUrl('ESDC'));
            this.sdc.initEsdc({ options });
        }
    }
    async verifyPin(pin, sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc === 'VSDC')
            return { error: { code: 'not_supported' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.verifyPin({ data: pin, options: options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async createInvoice(invoice, sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.createInvoice({ data: invoice, options: options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async getInvoice(requestId, sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.getInvoice({ data: requestId, options: options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async getEnvironmentParameters(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.getEnvironmentParameters({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async getStatus(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.getStatus({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async getAttention(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.getAttention({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async getUiData(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc !== 'ESDC')
            return { error: { code: 'supported_only_on_esdc' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.getUiData({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async sendPendingAuditPackages(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc !== 'ESDC')
            return { error: { code: 'supported_only_on_esdc' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.sendPendingAuditPackages({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async reset(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc !== 'ESDC')
            return { error: { code: 'supported_only_on_esdc' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.reset({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async storeLocalAuditFiles(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc !== 'ESDC')
            return { error: { code: 'supported_only_on_esdc' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.storeLocalAuditFiles({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async executeCommandsFromStorage(sdc) {
        const url = this.getSdcUrl(sdc);
        const useLocalEsdc = sdc === 'ESDC' && !this.isRemoteUrl(url);
        if (useLocalEsdc && !this.deviceSupported)
            return { error: { code: 'not_supported_on_device', message: I18n.t('sdc.status.not_supported_on_device') } };
        if (useLocalEsdc && !this.permissionsAllowed)
            return { error: { code: 'missing_permissions', message: I18n.t('sdc.status.missing_permissions') } };
        if (sdc !== 'ESDC')
            return { error: { code: 'supported_only_on_esdc' } };
        try {
            const options = this.buildPluginOptions(sdc, url);
            return this.sdc.executeCommandsFromStorage({ options });
        }
        catch (error) {
            return { error: { code: 'exception' } };
        }
    }
    async chooseCertificate(password, pac) {
        const options = {
            url: this.getSdcUrl('VSDC'),
            pac: pac,
            certAlias: undefined,
            sdc: 'VSDC',
            // iOS imports the picked .pfx/.p12 with this password
            // (SecPKCS12Import). Gated so the Android plugin payload stays
            // byte-identical to the pre-iOS-port shape.
            ...(Device.isIOS() ? { password } : {}),
        };
        const certSelectResult = await this.sdc.chooseCertificate({ options });
        // The password is only needed for the PKCS#12 import above — don't
        // let it ride along into subsequent plugin calls.
        delete options.password;
        if (!certSelectResult.error) {
            const certAlias = certSelectResult.data?.cert;
            const certExpDate = certSelectResult.data?.expirationDate;
            options.certAlias = certAlias;
            const statusResult = await this.sdc.getStatus({ options });
            if (!statusResult.error) {
                const appStorage = diContainer.resolve('appStorage');
                appStorage.native().setItem(AppStorageKey.sdcCertPac, pac);
                appStorage.native().setItem(AppStorageKey.sdcCertAlias, certAlias);
                appStorage.native().setItem(AppStorageKey.sdcCertExpirationDate, certExpDate);
                this.pac = pac;
                this.certAlias = certAlias;
                return { success: true, ...certSelectResult.data };
            }
        }
        return { success: false };
    }
    getSdcUrl(sdc) {
        if (sdc === 'VSDC')
            return Settings.env.VSDC_URL;
        else if (sdc === 'ESDC')
            return this.sdcUrl || Settings.env.ESDC_URL;
        else
            return Settings.env.ESDC_DEV_URL;
    }
    async checkPermissions() {
        let permissions = await this.sdc.checkPermissions();
        if (permissions.esdc !== 'granted') {
            permissions = await this.sdc.requestPermissions();
        }
        return permissions.esdc === 'granted';
    }
    /**
     * Checks if the given URL string points to a remote server (not localhost)
     * @param urlString the URL to check
     * @returns true if the URL is remote, false if it's localhost or malformed
     */
    isRemoteUrl(urlString) {
        if (!urlString || urlString.trim() === '') {
            return false;
        }
        try {
            const url = new URL(urlString);
            const host = url.hostname.toLowerCase();
            // Check for common localhost identifiers
            const isLocalhost = host === 'localhost' ||
                host === '127.0.0.1' ||
                host === '::1' ||
                host === '0.0.0.0' ||
                host.startsWith('127.') ||
                /.*\.local$/.test(host);
            return !isLocalhost;
        }
        catch (error) {
            return false;
        }
    }
}
//# sourceMappingURL=FiscalServiceAndroid.js.map